import { UserPlus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MarkShippedDialog } from "@/components/shipments/mark-shipped-dialog"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { shippableVariantsFor } from "@/lib/shippable-variants"
import { cn } from "@/lib/utils"
import { assignMissionPath } from "@/routes"
import type { BrandMissionCollabsPanel } from "@/types/missions"

import { BrandMissionCollabRowActions } from "./brand-mission-collab-row-actions"
import { CollabStatePill } from "./collab-state-pill"

/**
 * Brand-side Collabs panel — UX Spec §2.2.
 *
 * Anchored below the brief/files cards on `/brand/missions/:id`. Renders the
 * server-side collab list with state pill + last-state-change time + per-row
 * actions. Read-only when `panel.can_act === false` (OKS Admin inspect mode
 * or non-actionable role contexts).
 *
 * Responsive (UX Spec §5):
 *   - ≥768px: shadcn `Table` with comfortable column gap.
 *   - <768px:  the same data renders as stacked rows (`Card`-style stacks
 *              via `md:hidden` / `hidden md:table-row` toggling).
 *
 * Empty states:
 *   - 0 collabs → muted block with `UserPlus` icon + Assign CTA link
 *     (UX Spec §2.2 inline empty hint).
 *   - >0 collabs → table.
 */
interface BrandMissionCollabsPanelProps {
  panel: BrandMissionCollabsPanel
  missionId: number
  /** OKS-35 — opens the BrandSubmissionsSheet for the given collab. */
  onViewSubmissions?: (collabId: number) => void
}

export function BrandMissionCollabsPanelCard({
  panel,
  missionId,
  onViewSubmissions,
}: BrandMissionCollabsPanelProps) {
  const { t } = useTranslation()
  const isEmpty = panel.rows.length === 0

  return (
    <Card data-testid="brand-mission-collabs-panel">
      <CardHeader>
        <CardTitle>
          {t("missions.show.collabs.title", { count: panel.total_count })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <EmptyState missionId={missionId} />
        ) : (
          <>
            {/* Desktop / tablet — table layout (≥md) */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t("missions.show.collabs.col_creator")}
                    </TableHead>
                    <TableHead>
                      {t("missions.show.collabs.col_state")}
                    </TableHead>
                    <TableHead>
                      {t("missions.show.collabs.col_last_activity")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("missions.show.collabs.col_actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {panel.rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-testid="brand-mission-collab-row"
                      data-collab-id={row.id}
                      data-state={row.state}
                    >
                      <TableCell>
                        <CreatorCell row={row} />
                      </TableCell>
                      <TableCell>
                        <StatePillWithIssueNote row={row} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        <LastActivity row={row} missionId={missionId} />
                      </TableCell>
                      <TableCell className="text-right">
                        <BrandMissionCollabRowActions
                          row={row}
                          missionId={missionId}
                          onViewSubmissions={onViewSubmissions}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile — stacked rows (<md) */}
            <ul className="flex flex-col gap-3 md:hidden">
              {panel.rows.map((row) => (
                <li
                  key={row.id}
                  data-testid="brand-mission-collab-row-mobile"
                  data-collab-id={row.id}
                  data-state={row.state}
                  className="border-input flex flex-col gap-2 rounded-md border p-3"
                >
                  <CreatorCell row={row} />
                  <div className="flex items-center justify-between gap-2">
                    <StatePillWithIssueNote row={row} />
                    <span className="text-muted-foreground text-xs">
                      <LastActivity row={row} missionId={missionId} />
                    </span>
                  </div>
                  <div>
                    <BrandMissionCollabRowActions
                      row={row}
                      missionId={missionId}
                    />
                  </div>
                </li>
              ))}
            </ul>

            {panel.total_count > panel.rows.length && (
              <p
                className="text-muted-foreground mt-4 text-center text-xs"
                data-testid="brand-mission-collabs-load-more"
              >
                {t("missions.show.collabs.load_more_hint", {
                  showing: panel.rows.length,
                  total: panel.total_count,
                })}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function EmptyState({ missionId }: { missionId: number }) {
  const { t } = useTranslation()
  return (
    <div
      className="flex flex-col items-center gap-2 rounded-md border border-dashed py-8 text-center"
      data-testid="brand-mission-collabs-empty"
    >
      <UserPlus aria-hidden="true" className="text-muted-foreground size-6" />
      <p className="text-muted-foreground text-sm">
        {t("missions.show.collabs.empty_title")}
      </p>
      <a
        href={assignMissionPath(missionId)}
        className="text-primary text-sm underline-offset-4 hover:underline"
      >
        {t("missions.show.collabs.empty_cta")}
      </a>
    </div>
  )
}

function CreatorCell({
  row,
}: {
  row: BrandMissionCollabsPanel["rows"][number]
}) {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-2">
      <Avatar className="size-8">
        <AvatarFallback>
          {row.creator.name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col">
        <span className="text-foreground text-sm font-medium">
          {row.creator.name}
        </span>
        {row.creator.handle && (
          <span className="text-muted-foreground text-xs">
            @{row.creator.handle}
          </span>
        )}
        {/*
          OKS-984 (UX §2.3 / PRD OQ-7) — a COMPACT destination line, not the
          full block. The table is already dense at 402px, and the full address
          is needed at the moment of packing, which is the dialog. The
          Shipments page is the surface that shows the block at rest.

          ⚠️ Branch on PRESENCE — the key is ABSENT outside the reveal window,
          exactly like `creator_sizes`.
        */}
        {"shipping_address" in row && row.shipping_address && (
          <span
            className="text-muted-foreground text-xs"
            data-testid="brand-mission-collab-ship-to"
          >
            {t("shipments.address.ship_to")}: {row.shipping_address.city},{" "}
            {row.shipping_address.country}
          </span>
        )}
      </div>
    </div>
  )
}

function StatePillWithIssueNote({
  row,
}: {
  row: BrandMissionCollabsPanel["rows"][number]
}) {
  if (row.state === "issue" && row.issue_note) {
    return (
      <Tooltip>
        {/* The pill is the trigger — focusable for keyboard users so the
            note is reachable without a hover. */}
        <TooltipTrigger
          render={
            <button
              type="button"
              className={cn(
                "inline-flex items-center rounded-full",
                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
              )}
              data-testid="brand-mission-collab-issue-pill"
            >
              <CollabStatePill state={row.state} />
            </button>
          }
        />
        <TooltipContent
          side="top"
          className="max-w-xs whitespace-pre-wrap"
          data-testid="brand-mission-collab-issue-tooltip"
        >
          {row.issue_note}
        </TooltipContent>
      </Tooltip>
    )
  }

  return <CollabStatePill state={row.state} />
}

function LastActivity({
  row,
  missionId,
}: {
  row: BrandMissionCollabsPanel["rows"][number]
  missionId: number
}) {
  const { t, i18n } = useTranslation()

  const date = row.last_state_change_at
    ? new Date(row.last_state_change_at)
    : null
  // Concise locale-aware date — full timestamp lives in the data attr if
  // a future surface needs it.
  const formatted = date
    ? date.toLocaleDateString(i18n.language === "en" ? "en-US" : "fr-FR", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null

  // OKS-32 — Tracking-number rendering on `shipped` / `delivered` Drop
  // rows. The string sits on the same line as the relative-time copy
  // ("Shipped 2h ago · TRK-DROP-OKS32"), truncated at 24 chars on
  // mobile with a tooltip for the full value (UX Spec §5).
  const showsTracking =
    (row.state === "shipped" || row.state === "delivered") &&
    !!row.tracking_number

  // OKS-996 (BRIEF D6, AC8) — THE CORRECTION TRIGGER IS NO LONGER TIED TO THE
  // TRACKING STRING.
  //
  // It used to render inside the `showsTracking` block, so a collab shipped
  // WITHOUT a tracking number had no pencil here — and the queue's
  // Recently-shipped section keeps only the last 20 rows
  // (`Shipments::Query::RECENT_SHIPPED`), so once such a row fell off that list
  // it was correctable from nowhere at all. Shipping without tracking is
  // ordinary (PRD §1: small packages), which made "every shipped collab is
  // correctable" false for a whole row class rather than an edge.
  //
  // Its own gate now, on the row's state and the server's own permission.
  const showsCorrection = row.state === "shipped" && row.can_mark_shipped

  // ⚠️ THIS EARLY RETURN IS THE ACTUAL GATE, and adding `showsCorrection` to it
  // is not defensive tidying. A `shipped` row with no tracking string and no
  // formatted date returns `—` here, BEFORE the JSX below runs — so widening
  // the trigger's own condition without widening this one leaves it unreachable
  // for exactly the row class D6 exists for, while the code below reads as
  // correct. LQE's arm walks a row with neither.
  if (!formatted && !showsTracking && !showsCorrection) return <>—</>

  return (
    <div className="flex items-center gap-1.5">
      {formatted && (
        <span
          title={t("missions.show.collabs.last_activity", { date: formatted })}
        >
          {formatted}
        </span>
      )}
      {showsTracking && row.tracking_number && (
        <>
          {formatted && <span aria-hidden="true">·</span>}
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  className="max-w-[12ch] truncate font-mono text-xs"
                  data-testid="brand-mission-collab-tracking-display"
                >
                  {row.tracking_number}
                </span>
              }
            />
            <TooltipContent
              side="top"
              className="font-mono"
              data-testid="brand-mission-collab-tracking-tooltip"
            >
              {row.tracking_number}
            </TooltipContent>
          </Tooltip>
        </>
      )}
      {/* OKS-996 — a SIBLING of the tracking cluster, not a child of it.
          OKS-995: the set comes from the ROW's own product, so this door and
          the queue's offer the same thing for the same row because they read
          the same per-collab payload — not because a shared prop happens to
          hold. */}
      {showsCorrection && (
        <>
          {/* ⚠️ `formatted !== null`, not a truthiness check on `formatted`.
              The separator is about whether anything was rendered BEFORE this
              trigger, and eslint's nullish-coalescing autofix here
              (`formatted ?? showsTracking`) is not equivalent: it would treat
              an empty-string date as "nothing before" while the `<span>` above
              still rendered. `formatted` is `string | null` today, so the two
              agree — this spells out the intent so the next formatter change
              cannot quietly pick the other one. */}
          {(formatted !== null || showsTracking) && (
            <span aria-hidden="true">·</span>
          )}
          <MarkShippedDialog
            row={row}
            target={{ kind: "collab", missionId }}
            mode="edit"
            variantOptions={shippableVariantsFor(row.product)}
            product={row.product ?? null}
          />
        </>
      )}
    </div>
  )
}
